import { ArrowUpRight, GraduationCap, Globe2, Building2, HardHat, UtensilsCrossed, BookOpen } from "lucide-react";

const CaseStudies = () => {
    const caseStudies = [
        {
            title: "Destination Aurora",
            category: "GovTech + Tourism",
            tagline: "One-stop digital ecosystem for Downtown Aurora, IL",
            description:
                "Smart-city government platform paired with a mobile app serving residents and visitors — entertainment, dining, events, parking, lodging, and directory.",
            website: "Town of Aurora, IL",
            url: "https://www.aurora-il.org",
            Icon: Building2,
            accent: "bg-cyan-500",
            features: [
                "Mobile: Entertainment, Dining, Events",
                "Smart Parking & Downtown Directory",
                "Digital Twin 3D Visualization",
                "Citizen Self-Service & Permits",
            ],
            impact: { metric: "60%", label: "Faster Permit Processing" },
            secondaryMetrics: [
                { value: "6", label: "Integrated Services" },
                { value: "100%", label: "Digital Tourism Coverage" },
            ],
        },
        {
            title: "K12 ERP",
            category: "EdTech · K-12",
            tagline: "The complete K-12 district operations platform",
            description:
                "13 integrated modules, 150+ sub-modules, AI throughout — built exclusively for K-12 districts. From hiring through retirement, with AI woven in.",
            website: "www.k12erp.com",
            url: "https://www.k12erp.com",
            Icon: GraduationCap,
            accent: "bg-blue-500",
            features: [
                "13 Integrated Modules",
                "150+ Sub-Modules",
                "AI-Powered Throughout",
                "IL-Ready (TRS / IMRF / SOPPA / BIPA)",
            ],
            impact: { metric: "$2.5M", label: "Avg. Annual Savings per District" },
            secondaryMetrics: [
                { value: "40%", label: "Admin Time Saved" },
                { value: "85%", label: "Process Automation" },
            ],
        },
        {
            title: "Construction Intelligence",
            category: "Construction Tech",
            tagline: "AI-powered project management for modern builders",
            description:
                "Streamlines project tracking, resource allocation, and predictive maintenance across large construction portfolios.",
            website: "Enterprise Engagement",
            url: null,
            Icon: HardHat,
            accent: "bg-amber-500",
            features: [
                "AI Project Scheduling",
                "Real-Time Resource Tracking",
                "Predictive Maintenance",
                "Automated Progress Reporting",
            ],
            impact: { metric: "45%", label: "Reduction in Project Delays" },
        },
        {
            title: "Campus USA",
            category: "HigherEd + AI Talent",
            tagline: "The Global AI Talent Ecosystem",
            description:
                "Where students build world-class AI skills, universities enhance outcomes, and companies access verified, deployment-ready talent.",
            website: "www.campususaedu.com",
            url: "https://www.campususaedu.com",
            Icon: Globe2,
            accent: "bg-violet-500",
            features: [
                "20+ University Partners",
                "15 AI Training Tracks",
                "300 Training Modules",
                "11 Live Projects",
            ],
            impact: { metric: "40–60%", label: "Cost Savings for Universities" },
            secondaryMetrics: [
                { value: "20+", label: "Partner Universities" },
                { value: "300", label: "Training Modules" },
            ],
        },
        {
            title: "Smart Restaurant Ops",
            category: "Hospitality Tech",
            tagline: "Intelligent F&B operations, end-to-end",
            description:
                "AI-driven inventory, menu optimization, staff scheduling, and customer analytics for multi-location restaurant groups.",
            website: "Enterprise Engagement",
            url: null,
            Icon: UtensilsCrossed,
            accent: "bg-orange-500",
            features: [
                "AI Inventory & Waste Prediction",
                "Smart Menu Optimization",
                "Automated Staff Scheduling",
                "Customer Preference Analytics",
            ],
            impact: { metric: "35%", label: "Operating Cost Reduction" },
        },
        {
            title: "Corporate Learning",
            category: "Enterprise L&D",
            tagline: "AI-personalized enterprise learning",
            description:
                "Personalized learning paths, skill-gap analysis, interactive workshops, and certification management for enterprise L&D teams.",
            website: "Enterprise Engagement",
            url: null,
            Icon: BookOpen,
            accent: "bg-pink-500",
            features: [
                "AI-Personalized Paths",
                "Skill Gap Analysis",
                "Interactive Virtual Workshops",
                "Automated Certifications",
            ],
            impact: { metric: "3x", label: "Faster Skill Development" },
        },
    ];

    const handleCardClick = (url) => {
        if (url) window.open(url, "_blank", "noopener,noreferrer");
        else document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section id="work" className="relative py-32 border-t border-white/5 bg-ink-900/40">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-4">
                            / Selected Work
                        </div>
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-display leading-[0.95] tracking-tight text-white max-w-3xl">
                            Real platforms.
                            <br />
                            <em className="italic text-white/50">Across every</em>
                            <br />
                            institution type.
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md text-[15px] leading-relaxed">
                        Government, higher-ed, K-12, hospitality, construction, corporate learning — production systems, not concept slides.
                    </p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {caseStudies.map((s, i) => (
                        <button
                            key={i}
                            onClick={() => handleCardClick(s.url)}
                            className="group text-left rounded-2xl border border-white/10 bg-ink-950 hover:bg-ink-800 p-8 md:p-10 transition-all duration-500 relative overflow-hidden card-shine"
                            data-testid={`case-study-card-${i}`}
                        >
                            {/* Top row */}
                            <div className="flex items-start justify-between mb-8">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg ${s.accent}/10 border border-white/10 flex items-center justify-center`}>
                                        <s.Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                                            {s.category}
                                        </span>
                                    </div>
                                </div>
                                <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-accent-brand group-hover:border-accent-brand transition-all duration-300">
                                    <ArrowUpRight className="w-4 h-4 text-white/70 group-hover:text-ink-950 transition-colors" />
                                </div>
                            </div>

                            {/* Title */}
                            <h3 className="text-3xl md:text-4xl font-display text-white tracking-tight mb-2">
                                {s.title}
                            </h3>
                            <div className="text-[13px] font-mono text-accent-brand/80 mb-4">
                                {s.website}
                            </div>
                            <p className="text-white/60 text-[15px] leading-relaxed mb-6 max-w-lg">
                                {s.tagline}
                            </p>

                            {/* Feature chips */}
                            <div className="flex flex-wrap gap-2 mb-8">
                                {s.features.map((f, fi) => (
                                    <span
                                        key={fi}
                                        className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10 text-white/60"
                                    >
                                        {f}
                                    </span>
                                ))}
                            </div>

                            {/* Metrics row */}
                            <div className="flex items-end justify-between pt-6 border-t border-white/5">
                                <div>
                                    <div className="text-4xl md:text-5xl font-display text-white leading-none tracking-tight">
                                        {s.impact.metric}
                                    </div>
                                    <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 mt-2">
                                        {s.impact.label}
                                    </div>
                                </div>
                                {s.secondaryMetrics && (
                                    <div className="flex gap-6">
                                        {s.secondaryMetrics.map((m, mi) => (
                                            <div key={mi} className="text-right">
                                                <div className="text-xl font-display text-white/80">
                                                    {m.value}
                                                </div>
                                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/30 mt-1">
                                                    {m.label}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CaseStudies;
